import React, { useState, useEffect } from 'react';

const MenuItemForm = ({
  categories,
  onSubmit,
  editingItem,
  onSaveItem,
  onCancelEdit,
  variantGroups = [],
  supplies = [],
  onVariantGroupsChange
}) => {

  const [name, setName]                         = useState('');
  const [price, setPrice]                       = useState('');
  const [tax, setTax]                           = useState('16');
  const [priceWithoutVAT, setPriceWithoutVAT]   = useState(0);
  const [categoryId, setCategoryId]             = useState('');
  const [image, setImage]                       = useState('');
  const [assignedGroups, setAssignedGroups]     = useState([]);

  const [showNewGroup, setShowNewGroup]             = useState(false);
  const [newGroupName, setNewGroupName]             = useState('');
  const [newGroupType, setNewGroupType]             = useState('substitute');
  const [newGroupOptions, setNewGroupOptions]       = useState([]);
  const [newGroupBaseVolume, setNewGroupBaseVolume] = useState('');
  const [newGroupVolumeUnit, setNewGroupVolumeUnit] = useState('ml');
  const [savingGroup, setSavingGroup]               = useState(false);

  const [editingGroupId, setEditingGroupId]           = useState(null);
  const [editGroupName, setEditGroupName]             = useState('');
  const [editGroupType, setEditGroupType]             = useState('substitute');
  const [editGroupOptions, setEditGroupOptions]       = useState([]);
  const [editGroupBaseVolume, setEditGroupBaseVolume] = useState('');
  const [editGroupVolumeUnit, setEditGroupVolumeUnit] = useState('ml');
  const [savingEditGroup, setSavingEditGroup]         = useState(false);

  /* ── Cargar datos al editar producto ── */
  useEffect(() => {
    if (editingItem) {
      setName(editingItem.name);
      setPrice(editingItem.price.toString());
      setTax(editingItem.tax?.toString() || '16');
      setPriceWithoutVAT(editingItem.priceWithoutVAT || 0);
      setCategoryId(editingItem.categoryId);
      // FIX: cargar image_url correctamente — la API devuelve image_url no image
      setImage(editingItem.image_url || editingItem.image || '');
      fetch(`/api/variants/get_menu_item_variants.php?menu_item_id=${editingItem.id}`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setAssignedGroups(data.groups.map(g => ({
              variant_group_id: g.variant_group_id,
              is_required:      g.is_required
            })));
          }
        })
        .catch(console.error);
    } else {
      resetForm();
    }
  }, [editingItem]);

  /* ── Precio sin IVA ── */
  useEffect(() => {
    if (price) {
      const divisor = 1 + (parseFloat(tax) || 0) / 100;
      setPriceWithoutVAT(divisor > 0 ? parseFloat(price) / divisor : 0);
    } else {
      setPriceWithoutVAT(0);
    }
  }, [price, tax]);

  const resetForm = () => {
    setName(''); setPrice(''); setTax('16'); setPriceWithoutVAT(0);
    setCategoryId(''); setImage(''); setAssignedGroups([]);
    setShowNewGroup(false); setNewGroupName(''); setNewGroupType('substitute');
    setNewGroupOptions([]); setNewGroupBaseVolume(''); setNewGroupVolumeUnit('ml');
    setEditingGroupId(null);
  };

  const refreshGroups = async () => {
    const res  = await fetch('/api/variants/get_variant_groups.php');
    const data = await res.json();
    if (data.success && onVariantGroupsChange) onVariantGroupsChange(data.groups || []);
  };

  /* ── Toggle asignación ── */
  const handleToggleGroup = (group_id) => {
    setAssignedGroups(prev => {
      const exists = prev.find(g => g.variant_group_id === group_id);
      if (exists) return prev.filter(g => g.variant_group_id !== group_id);
      return [...prev, { variant_group_id: group_id, is_required: false }];
    });
  };

  const handleToggleRequired = (group_id) => {
    setAssignedGroups(prev =>
      prev.map(g => g.variant_group_id === group_id
        ? { ...g, is_required: !g.is_required } : g)
    );
  };

  /* ── Helpers opciones nuevo grupo ── */
  const handleAddNewOption = () => {
    setNewGroupOptions(prev => [...prev, {
      name: '', price_modifier: 0,
      effect: { supply_id_from: null, supply_id_to: null, target_volume: null }
    }]);
  };
  const handleNewOptionChange = (idx, field, value) => {
    setNewGroupOptions(prev => prev.map((opt, i) => i === idx ? { ...opt, [field]: value } : opt));
  };
  const handleNewEffectChange = (idx, field, value) => {
    setNewGroupOptions(prev => prev.map((opt, i) =>
      i === idx ? { ...opt, effect: { ...opt.effect, [field]: value === '' ? null : value } } : opt
    ));
  };
  const handleRemoveNewOption = (idx) => {
    setNewGroupOptions(prev => prev.filter((_, i) => i !== idx));
  };

  /* ── Helpers opciones editar grupo ── */
  const handleAddEditOption = () => {
    setEditGroupOptions(prev => [...prev, {
      name: '', price_modifier: 0,
      effect: { supply_id_from: null, supply_id_to: null, target_volume: null }
    }]);
  };
  const handleEditOptionChange = (idx, field, value) => {
    setEditGroupOptions(prev => prev.map((opt, i) => i === idx ? { ...opt, [field]: value } : opt));
  };
  const handleEditEffectChange = (idx, field, value) => {
    setEditGroupOptions(prev => prev.map((opt, i) =>
      i === idx ? { ...opt, effect: { ...opt.effect, [field]: value === '' ? null : value } } : opt
    ));
  };
  const handleRemoveEditOption = (idx) => {
    setEditGroupOptions(prev => prev.filter((_, i) => i !== idx));
  };

  /* ── Abrir edición de grupo existente ── */
  const handleOpenEditGroup = (group) => {
    setEditingGroupId(group.id);
    setEditGroupName(group.name);
    setEditGroupType(group.type);
    setEditGroupBaseVolume(group.base_volume ?? '');
    setEditGroupVolumeUnit(group.volume_unit ?? 'ml');
    setEditGroupOptions((group.options || []).map(opt => ({
      name:           opt.name,
      price_modifier: opt.price_modifier,
      effect: {
        supply_id_from: opt.effect?.supply_id_from ?? null,
        supply_id_to:   opt.effect?.supply_id_to   ?? null,
        target_volume:  opt.effect?.target_volume  ?? null,
      }
    })));
    setShowNewGroup(false);
  };

  const handleCancelEditGroup = () => setEditingGroupId(null);

  /* ── Guardar edición de grupo ── */
  const handleSaveEditGroup = async () => {
    if (!editGroupName.trim() || editGroupOptions.length === 0) {
      alert('Nombre y al menos una opción son requeridos'); return;
    }
    if (editGroupType === 'size' && !editGroupBaseVolume) {
      alert('Debes ingresar el volumen base'); return;
    }
    for (const opt of editGroupOptions) {
      if (!opt.name.trim()) { alert('Todas las opciones necesitan nombre'); return; }
      if (editGroupType === 'substitute' && (!opt.effect.supply_id_from || !opt.effect.supply_id_to)) {
        alert(`La opción "${opt.name}" necesita insumo origen e insumo destino`); return;
      }
      if (editGroupType === 'size' && !opt.effect.target_volume) {
        alert(`La opción "${opt.name}" necesita el volumen del tamaño`); return;
      }
    }

    setSavingEditGroup(true);
    try {
      const payload = {
        id:   editingGroupId,
        name: editGroupName,
        type: editGroupType,
        ...(editGroupType === 'size' && {
          base_volume: Number(editGroupBaseVolume) || null,
          volume_unit: editGroupVolumeUnit
        }),
        options: editGroupOptions.map(opt => ({
          name:           opt.name,
          price_modifier: Number(opt.price_modifier) || 0,
          effect: editGroupType === 'substitute'
            ? { supply_id_from: Number(opt.effect.supply_id_from), supply_id_to: Number(opt.effect.supply_id_to) }
            : { target_volume: Number(opt.effect.target_volume), base_volume: Number(editGroupBaseVolume) }
        }))
      };

      const res  = await fetch('/api/variants/save_variant_group.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!data.success) { alert(data.message || 'Error actualizando grupo'); return; }
      await refreshGroups();
      setEditingGroupId(null);
      alert('Grupo actualizado');
    } catch (e) {
      console.error(e); alert('Error de servidor');
    } finally {
      setSavingEditGroup(false);
    }
  };

  /* ── Eliminar grupo ── */
  const handleDeleteGroup = async (group) => {
    if (!window.confirm(`¿Eliminar el grupo "${group.name}" y todas sus opciones?`)) return;
    try {
      const res  = await fetch('/api/variants/delete_variant_group.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: group.id })
      });
      const data = await res.json();
      if (!data.success) { alert(data.message || 'Error eliminando'); return; }
      await refreshGroups();
      setAssignedGroups(prev => prev.filter(g => g.variant_group_id !== group.id));
      if (editingGroupId === group.id) setEditingGroupId(null);
    } catch (e) {
      console.error(e);
    }
  };

  /* ── Guardar nuevo grupo ── */
  const handleSaveNewGroup = async () => {
    if (!newGroupName.trim() || newGroupOptions.length === 0) {
      alert('Nombre y al menos una opción son requeridos'); return;
    }
    if (newGroupType === 'size' && !newGroupBaseVolume) {
      alert('Debes ingresar el volumen base de la receta'); return;
    }
    for (const opt of newGroupOptions) {
      if (!opt.name.trim()) { alert('Todas las opciones necesitan nombre'); return; }
      if (newGroupType === 'substitute' && (!opt.effect.supply_id_from || !opt.effect.supply_id_to)) {
        alert(`La opción "${opt.name}" necesita insumo origen e insumo destino`); return;
      }
      if (newGroupType === 'size' && !opt.effect.target_volume) {
        alert(`La opción "${opt.name}" necesita el volumen del tamaño`); return;
      }
    }

    setSavingGroup(true);
    try {
      const payload = {
        name: newGroupName, type: newGroupType,
        ...(newGroupType === 'size' && {
          base_volume: Number(newGroupBaseVolume) || null,
          volume_unit: newGroupVolumeUnit
        }),
        options: newGroupOptions.map(opt => ({
          name:           opt.name,
          price_modifier: Number(opt.price_modifier) || 0,
          effect: newGroupType === 'substitute'
            ? { supply_id_from: Number(opt.effect.supply_id_from), supply_id_to: Number(opt.effect.supply_id_to) }
            : { target_volume: Number(opt.effect.target_volume), base_volume: Number(newGroupBaseVolume) }
        }))
      };

      const res  = await fetch('/api/variants/save_variant_group.php', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!data.success) { alert(data.message || 'Error guardando grupo'); return; }
      await refreshGroups();
      setAssignedGroups(prev => [...prev, { variant_group_id: data.group_id, is_required: false }]);
      setShowNewGroup(false); setNewGroupName(''); setNewGroupType('substitute');
      setNewGroupOptions([]); setNewGroupBaseVolume(''); setNewGroupVolumeUnit('ml');
      alert('Grupo creado y asignado al producto');
    } catch (e) {
      console.error(e); alert('Error de servidor');
    } finally {
      setSavingGroup(false);
    }
  };

  /* ── Submit producto ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !price || !categoryId || !tax) return;

    const formData = new FormData();
    if (editingItem) formData.append('id', editingItem.id);
    formData.append('name', name);
    formData.append('price', parseFloat(price));
    formData.append('tax', parseFloat(tax));
    formData.append('priceWithoutVAT', priceWithoutVAT);
    formData.append('categoryId', categoryId);

    if (image instanceof File) {
      // FIX: archivo nuevo — subir
      formData.append('image', image);
    } else if (typeof image === 'string' && image.trim() !== '') {
      // FIX: URL existente o nueva — enviar como image_url para que update_product.php la guarde
      formData.append('image_url', image);
    }
    // FIX: si image está vacío no se manda nada — update_product.php no toca image_url

    let savedItemId = editingItem?.id ?? null;
    if (editingItem) {
      onSaveItem(formData);
    } else {
      const newId = await onSubmit(formData);
      savedItemId = newId ?? null;
    }

    if (savedItemId) {
      try {
        await fetch('/api/variants/save_menu_item_variants.php', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ menu_item_id: savedItemId, groups: assignedGroups })
        });
      } catch (err) {
        console.error('Error guardando variantes:', err);
      }
    }
    resetForm();
  };

  // FIX: handlers de imagen correctos
  const handleImageURLChange  = (e) => setImage(e.target.value);
  const handleImageFileChange = (e) => { if (e.target.files[0]) setImage(e.target.files[0]); };

  /* ── Helper reutilizable para opciones de grupo ── */
  const renderOptions = (options, type, baseVolume, volumeUnit, onOptionChange, onEffectChange, onRemove) => (
    options.map((opt, idx) => (
      <div key={idx} className="border border-gray-200 rounded p-2 mb-2 bg-[#F9EFD7]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-1">
          <div>
            <label className="block text-xs font-bold text-[#3F1907] mb-1">Nombre opción</label>
            <input
              type="text" value={opt.name}
              onChange={e => onOptionChange(idx, 'name', e.target.value)}
              placeholder="Ej: Grande"
              className="w-full px-2 py-1 border border-[#58735A] rounded text-xs bg-white text-[#3F1907]"
            />
          </div>
          <div>
            <label className="block text-xs font-bold text-[#3F1907] mb-1">
              Sobreprecio ($) <span className="font-normal text-gray-500">negativo = descuento</span>
            </label>
            <input
              type="number" step="0.50" value={opt.price_modifier}
              onChange={e => onOptionChange(idx, 'price_modifier', e.target.value)}
              className="w-full px-2 py-1 border border-[#58735A] rounded text-xs bg-white text-[#3F1907]"
            />
          </div>
        </div>

        {type === 'substitute' && (
          <div className="grid grid-cols-2 gap-2 mt-1">
            <div>
              <label className="block text-xs font-bold text-[#3F1907] mb-1">Reemplaza</label>
              <select
                value={opt.effect.supply_id_from ?? ''}
                onChange={e => onEffectChange(idx, 'supply_id_from', e.target.value)}
                className="w-full px-2 py-1 border border-[#58735A] rounded text-xs bg-white text-[#3F1907]"
              >
                <option value="">Insumo original</option>
                {(Array.isArray(supplies) ? supplies : []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-bold text-[#3F1907] mb-1">Por</label>
              <select
                value={opt.effect.supply_id_to ?? ''}
                onChange={e => onEffectChange(idx, 'supply_id_to', e.target.value)}
                className="w-full px-2 py-1 border border-[#58735A] rounded text-xs bg-white text-[#3F1907]"
              >
                <option value="">Insumo sustituto</option>
                {(Array.isArray(supplies) ? supplies : []).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {type === 'size' && (
          <div className="mt-1">
            <label className="block text-xs font-bold text-[#3F1907] mb-1">
              Volumen de este tamaño
              <span className="font-normal text-gray-500 ml-1">({volumeUnit})</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number" step="1" min="1"
                value={opt.effect.target_volume ?? ''}
                onChange={e => onEffectChange(idx, 'target_volume', e.target.value)}
                placeholder="350"
                className="w-32 px-2 py-1 border border-[#58735A] rounded text-xs bg-white text-[#3F1907]"
              />
              <span className="text-xs text-gray-500 whitespace-nowrap">
                {volumeUnit}
                {opt.effect.target_volume && baseVolume
                  ? ` → ×${(Number(opt.effect.target_volume) / Number(baseVolume)).toFixed(3)}`
                  : ''}
              </span>
            </div>
          </div>
        )}

        <button
          type="button" onClick={() => onRemove(idx)}
          className="text-xs text-red-500 hover:text-red-700 mt-1"
        >
          Eliminar opción
        </button>
      </div>
    ))
  );

  /* ── RENDER ── */
  return (
    <div className="bg-[#F9EFD7] p-4 rounded-lg shadow mb-4">
      <h3 className="text-lg font-semibold text-[#3F1907] mb-3">
        {editingItem ? 'Editar Producto' : 'Agregar Producto'}
      </h3>

      <form onSubmit={handleSubmit}>

        {/* NOMBRE */}
        <div className="mb-3">
          <label className="block text-[#3F1907] text-sm font-bold mb-1">Nombre del Producto</label>
          <input
            type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Nombre del producto" required
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
          />
        </div>

        {/* PRECIOS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="mb-3">
            <label className="block text-[#3F1907] text-sm font-bold mb-1">Precio (con IVA)</label>
            <input
              type="number" value={price} onChange={(e) => setPrice(e.target.value)}
              placeholder="Precio con IVA" min="0" step="0.01" required
              className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
            />
          </div>
          <div className="mb-3">
            <label className="block text-[#3F1907] text-sm font-bold mb-1">Precio sin IVA</label>
            <input type="number" value={priceWithoutVAT.toFixed(2)} readOnly
              className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
            />
          </div>
        </div>

        {/* IMPUESTO */}
        <div className="mb-3">
          <label className="block text-[#3F1907] text-sm font-bold mb-1">Impuesto (%)</label>
          <input
            type="number" value={tax} onChange={(e) => setTax(e.target.value)}
            min="0" step="0.01" required
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
          />
        </div>

        {/* IMAGEN */}
        <div className="mb-3">
          <label className="block text-[#3F1907] text-sm font-bold mb-1">Imagen del Producto</label>

          {/* FIX: preview de imagen actual al editar */}
          {editingItem && typeof image === 'string' && image.trim() !== '' && (
            <div className="mb-2 flex items-center gap-3">
              <img
                src={image}
                alt="Imagen actual"
                className="w-16 h-16 object-cover rounded-md border border-[#58735A]"
              />
              <div>
                <p className="text-xs text-[#58735A] font-bold">Imagen actual</p>
                <button
                  type="button"
                  onClick={() => setImage('')}
                  className="text-xs text-red-500 hover:text-red-700 mt-1"
                >
                  Quitar imagen
                </button>
              </div>
            </div>
          )}

          <input
            type="text"
            value={typeof image === 'string' ? image : ''}
            onChange={handleImageURLChange}
            placeholder="Pega la URL de la imagen (opcional)"
            className="w-full px-3 py-2 border border-[#58735A] rounded-md mb-2 bg-[#F9EFD7] text-[#3F1907]"
          />
          <div className="text-center text-xs text-gray-500 mb-2">o</div>
          <input
            type="file" accept="image/*"
            onChange={handleImageFileChange}
            className="w-full px-3 py-2 border border-[#58735A] rounded-md bg-[#F9EFD7] text-[#3F1907]"
          />
        </div>

        {/* CATEGORÍA */}
        <div className="mb-3">
          <label className="block text-[#3F1907] text-sm font-bold mb-1">Categoría</label>
          <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required
            className="w-full px-3 py-2 border border-[#58735A] rounded-md focus:outline-none focus:ring-2 focus:ring-[#3F1907] bg-[#F9EFD7] text-[#3F1907]"
          >
            <option value="">Selecciona una categoría</option>
            {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
          </select>
        </div>

        {/* ── VARIANTES ── */}
        <div className="mb-4 border border-[#58735A] rounded-md p-3">
          <div className="flex justify-between items-center mb-3">
            <label className="text-[#3F1907] text-sm font-bold">
              Variantes del producto
              <span className="text-xs font-normal text-gray-500 ml-1">(opcional)</span>
            </label>
            {!editingGroupId && (
              <button
                type="button"
                onClick={() => setShowNewGroup(v => !v)}
                className="text-xs bg-[#58735A] hover:bg-[#3F1907] text-white px-3 py-1 rounded-md"
              >
                {showNewGroup ? 'Cancelar' : '+ Nuevo grupo'}
              </button>
            )}
          </div>

          {/* Lista de grupos existentes */}
          {(Array.isArray(variantGroups) ? variantGroups : []).length === 0 && !showNewGroup ? (
            <p className="text-xs text-gray-500">No hay grupos de variantes. Crea uno con el botón de arriba.</p>
          ) : (
            <div className="space-y-2 mb-3">
              {(Array.isArray(variantGroups) ? variantGroups : []).map(group => {
                const assigned   = assignedGroups.find(g => g.variant_group_id === group.id);
                const isAssigned = !!assigned;
                const isEditing  = editingGroupId === group.id;

                return (
                  <div key={group.id} className="border border-[#58735A] rounded-md bg-white overflow-hidden">
                    <div className="flex items-center justify-between px-3 py-2">
                      <div>
                        <p className="text-sm font-semibold text-[#3F1907]">{group.name}</p>
                        <p className="text-xs text-gray-500">
                          {group.type === 'substitute'
                            ? 'Sustitución'
                            : `Tamaño (base: ${group.base_volume ?? '?'}${group.volume_unit ?? 'ml'})`}
                          {' · '}{group.options?.length || 0} opciones
                          {group.options?.length > 0 && (
                            <span className="ml-1 text-gray-400">
                              ({group.options.map(o => o.name).join(', ')})
                            </span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isAssigned && !isEditing && (
                          <label className="flex items-center gap-1 text-xs text-[#3F1907]">
                            <input
                              type="checkbox"
                              checked={!!assigned.is_required}
                              onChange={() => handleToggleRequired(group.id)}
                            />
                            Requerido
                          </label>
                        )}
                        {!isEditing && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleOpenEditGroup(group)}
                              className="text-xs text-blue-500 hover:text-blue-700 px-2 py-1"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteGroup(group)}
                              className="text-xs text-red-500 hover:text-red-700 px-2 py-1"
                            >
                              Eliminar
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleGroup(group.id)}
                              className={`text-xs px-3 py-1 rounded-full border transition ${
                                isAssigned
                                  ? 'bg-[#3F1907] text-[#F9EFD7] border-[#3F1907]'
                                  : 'bg-white text-[#3F1907] border-[#58735A] hover:bg-[#58735A] hover:text-white'
                              }`}
                            >
                              {isAssigned ? 'Quitar' : 'Asignar'}
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {isEditing && (
                      <div className="border-t border-[#58735A] p-3 bg-[#F9EFD7]">
                        <p className="text-xs font-bold text-[#3F1907] mb-2">Editando grupo</p>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                          <div>
                            <label className="block text-xs font-bold text-[#3F1907] mb-1">Nombre</label>
                            <input
                              type="text" value={editGroupName}
                              onChange={e => setEditGroupName(e.target.value)}
                              className="w-full px-2 py-1 border border-[#58735A] rounded text-sm bg-white text-[#3F1907]"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-bold text-[#3F1907] mb-1">Tipo</label>
                            <select
                              value={editGroupType}
                              onChange={e => { setEditGroupType(e.target.value); setEditGroupOptions([]); setEditGroupBaseVolume(''); }}
                              className="w-full px-2 py-1 border border-[#58735A] rounded text-sm bg-white text-[#3F1907]"
                            >
                              <option value="substitute">Sustitución de insumo</option>
                              <option value="size">Tamaño</option>
                            </select>
                          </div>
                        </div>

                        {editGroupType === 'size' && (
                          <div className="mb-3">
                            <label className="block text-xs font-bold text-[#3F1907] mb-1">
                              Volumen base <span className="font-normal text-gray-500">(ej: 240 para 8oz)</span>
                            </label>
                            <div className="flex gap-2">
                              <input
                                type="number" step="1" min="1"
                                value={editGroupBaseVolume}
                                onChange={e => setEditGroupBaseVolume(e.target.value)}
                                placeholder="240"
                                className="w-32 px-2 py-1 border border-[#58735A] rounded text-sm bg-white text-[#3F1907]"
                              />
                              <select
                                value={editGroupVolumeUnit}
                                onChange={e => setEditGroupVolumeUnit(e.target.value)}
                                className="px-2 py-1 border border-[#58735A] rounded text-sm bg-white text-[#3F1907]"
                              >
                                <option value="ml">ml</option>
                                <option value="oz">oz</option>
                              </select>
                            </div>
                          </div>
                        )}

                        {renderOptions(
                          editGroupOptions, editGroupType,
                          editGroupBaseVolume, editGroupVolumeUnit,
                          handleEditOptionChange, handleEditEffectChange, handleRemoveEditOption
                        )}

                        <div className="flex gap-2 mt-2">
                          <button
                            type="button" onClick={handleAddEditOption}
                            className="text-xs bg-[#58735A] hover:bg-[#3F1907] text-white px-3 py-1 rounded-md"
                          >
                            + Agregar opción
                          </button>
                          <button
                            type="button" onClick={handleSaveEditGroup}
                            disabled={savingEditGroup}
                            className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md disabled:opacity-50"
                          >
                            {savingEditGroup ? 'Guardando...' : 'Guardar cambios'}
                          </button>
                          <button
                            type="button" onClick={handleCancelEditGroup}
                            className="text-xs bg-gray-400 hover:bg-gray-500 text-white px-3 py-1 rounded-md"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Form nuevo grupo */}
          {showNewGroup && !editingGroupId && (
            <div className="border border-[#58735A] rounded-md p-3 bg-white mt-2">
              <p className="text-sm font-bold text-[#3F1907] mb-2">Nuevo grupo de variantes</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-xs font-bold text-[#3F1907] mb-1">Nombre del grupo</label>
                  <input
                    type="text" value={newGroupName}
                    onChange={e => setNewGroupName(e.target.value)}
                    placeholder="Ej: Tipo de leche"
                    className="w-full px-2 py-1 border border-[#58735A] rounded text-sm bg-[#F9EFD7] text-[#3F1907]"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#3F1907] mb-1">Tipo</label>
                  <select
                    value={newGroupType}
                    onChange={e => { setNewGroupType(e.target.value); setNewGroupOptions([]); setNewGroupBaseVolume(''); }}
                    className="w-full px-2 py-1 border border-[#58735A] rounded text-sm bg-[#F9EFD7] text-[#3F1907]"
                  >
                    <option value="substitute">Sustitución de insumo</option>
                    <option value="size">Tamaño</option>
                  </select>
                </div>
              </div>

              {newGroupType === 'size' && (
                <div className="mb-3">
                  <label className="block text-xs font-bold text-[#3F1907] mb-1">
                    Volumen base de la receta
                    <span className="font-normal text-gray-500 ml-1">(el tamaño estándar, ej: 240 para 8oz)</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="number" step="1" min="1" value={newGroupBaseVolume}
                      onChange={e => setNewGroupBaseVolume(e.target.value)}
                      placeholder="240"
                      className="w-32 px-2 py-1 border border-[#58735A] rounded text-sm bg-[#F9EFD7] text-[#3F1907]"
                    />
                    <select
                      value={newGroupVolumeUnit}
                      onChange={e => setNewGroupVolumeUnit(e.target.value)}
                      className="px-2 py-1 border border-[#58735A] rounded text-sm bg-[#F9EFD7] text-[#3F1907]"
                    >
                      <option value="ml">ml</option>
                      <option value="oz">oz</option>
                    </select>
                  </div>
                </div>
              )}

              {renderOptions(
                newGroupOptions, newGroupType,
                newGroupBaseVolume, newGroupVolumeUnit,
                handleNewOptionChange, handleNewEffectChange, handleRemoveNewOption
              )}

              <div className="flex gap-2 mt-2">
                <button
                  type="button" onClick={handleAddNewOption}
                  className="text-xs bg-[#58735A] hover:bg-[#3F1907] text-white px-3 py-1 rounded-md"
                >
                  + Agregar opción
                </button>
                <button
                  type="button" onClick={handleSaveNewGroup}
                  disabled={savingGroup}
                  className="text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md disabled:opacity-50"
                >
                  {savingGroup ? 'Guardando...' : 'Guardar grupo'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* BOTONES PRODUCTO */}
        <button type="submit"
          className="w-full bg-[#58735A] hover:bg-[#3F1907] text-[#F9EFD7] font-medium py-2 px-4 rounded-md transition duration-200"
        >
          {editingItem ? 'Actualizar Producto' : 'Guardar Producto'}
        </button>

        {editingItem && (
          <button type="button" onClick={onCancelEdit}
            className="w-full bg-gray-500 hover:bg-gray-600 text-[#F9EFD7] font-medium py-2 px-4 rounded-md transition duration-200 mt-2"
          >
            Cancelar Edición
          </button>
        )}

      </form>
    </div>
  );
};

export default MenuItemForm;